<template>
  <div ref="root" class="color-picker">
    <!-- Title Row -->
    <div
      :class="{
        'title-row': true,
        'color-picker__title-row': true,
      }"
    >
      <div class="title-row__title-text">
        {{ title }}
      </div>
      <div class="title-row__button-container">
        <fs-tooltip :text="showHexRgb ? 'Hide Hex/RGB' : 'Show Hex/RGB'">
          <icon-button
            size="small"
            :icon="icons.hashIcon"
            :class="{ 'title-row__toggle-button--active': showHexRgb }"
            @click="showHexRgb = !showHexRgb"
          />
        </fs-tooltip>
        <fs-tooltip v-if="removeFunc !== undefined" text="Remove color">
          <icon-button size="small" :icon="icons.trashCanIcon" @click="removeColor" />
        </fs-tooltip>
      </div>
    </div>
    <!-- / Title -->

    <!-- Color Search Form -->
    <div class="search-form color-picker__search">
      <fs-input
        v-model="searchInputValue"
        v-model:is-expanded="isSearchResultExpanded"
        class="search-form__input color-picker__search-field"
        placeholder="Find colors"
        position="left"
        is-expandable
        is-search
        @reset-input="searchInputValue = ''"
      />

      <!-- Which colours to look through: everything the brand can reach, one of its palettes, or
           one of the libraries it has switched on. Narrow until opened, joined to the field it
           filters — the same pair the components table's colour cell searches with. Offered
           before anything is typed too: picking a palette is a way of browsing it, not merely a
           way of qualifying a search term. -->
      <fs-select
        v-model="searchSource"
        class="search-form__select color-picker__search-source"
        :options="searchSourceOptions"
        value="value"
        position="right"
        :expanded-width-scale="2.4"
        :max-dropdown-height="220"
      />
    </div>
    <!--/  Color Search Form -->

    <!-- Where these values come from. Read-only: the number is the brand's to allocate, and a
         library entry is shared by every brand on it. A library value that differs from the
         picker's is offered as a way back to it. -->
    <div v-if="!showSearchResults" class="provenance color-picker__provenance">
      <div class="provenance__row">
        <span class="provenance__label">Number</span>
        <span :class="{ provenance__value: true, 'provenance__value--pending': !pickedNumber }">
          {{ pickedNumber || 'Generated when saved' }}
        </span>
      </div>
      <div v-if="pickerState.provenance.libraryName" class="provenance__row">
        <span class="provenance__label">Library</span>
        <span class="provenance__value">
          {{ pickerState.provenance.libraryName }}
          <template v-if="pickerState.provenance.libraryCode">
            ·
            <fs-tooltip v-if="recodedFromLibrary" text="Reset code to library">
              <span
                role="button"
                class="provenance__reset"
                @click="resetToLibrary({ code: pickerState.provenance.libraryCode! })"
              >
                {{ pickerState.provenance.libraryCode }}
              </span>
            </fs-tooltip>
            <template v-else>{{ pickerState.provenance.libraryCode }}</template>
          </template>
        </span>
      </div>
      <div v-if="renamedFromLibrary" class="provenance__row">
        <span class="provenance__label">Library name</span>
        <span class="provenance__value">
          <fs-tooltip text="Reset name to library">
            <span
              role="button"
              class="provenance__reset"
              @click="resetToLibrary({ name: pickerState.provenance.libraryColorName! })"
            >
              {{ pickerState.provenance.libraryColorName }}
            </span>
          </fs-tooltip>
        </span>
      </div>
    </div>

    <div class="color-picker__content-wrapper">
      <color-search-result
        v-if="showSearchResults"
        :class="{
          'color-picker__content': true,
          'color-picker__content--search-result': true,
        }"
        :search-query="searchInputValue"
        :source="searchSourceValue"
        @select-color="selectSearchResult"
      />

      <!-- Color Wheel Mode -->
      <div v-else class="color-picker__content">
        <!-- Color Wheel -->
        <!-- An image dropped here draws the colour instead of its flat RGB — a print. Only that one
             picture is handled here; the colour's other images are the Colors page's. -->
        <div
          :class="{
            'color-picker__color-wheel-container': true,
            'color-picker__color-wheel-container--drop-target': isDraggingImage,
          }"
          @dragenter="onSwatchDragEnter"
          @dragover="onSwatchDragOver"
          @dragleave="onSwatchDragLeave"
          @drop="onSwatchDrop"
        >
          <div v-if="pickerState.swatch.url" class="color-picker__swatch">
            <img
              class="color-picker__swatch-image"
              draggable="false"
              :src="imagePath(pickerState.swatch.url, 536, 536, 'fill')"
              :alt="localColor.name"
            />
            <fs-tooltip class="color-picker__swatch-remove" text="Use the flat color instead">
              <icon-button size="small" filled :icon="icons.trashCanIcon" @click="useFlatColor" />
            </fs-tooltip>
          </div>
          <color-picker-iro v-else v-model="localColor" :width="268" />

          <!-- Eye Dropper Button -->
          <div v-if="supportsEyeDropper && !pickerState.swatch.url" class="color-picker__eyedropper-button">
            <fs-tooltip text="Pick color from screen">
              <icon-button :icon="icons.eyeDropperIcon" @click="pickColorFromScreen" />
            </fs-tooltip>
          </div>
          <!-- / Eye Dropper Button -->

          <div v-if="isDraggingImage || isUploadingSwatch" class="color-picker__swatch-drop-hint">
            <circular-spinner-full v-if="isUploadingSwatch" :size="32" color="dark-blue" />
            <template v-else>Drop an image to use as the swatch</template>
          </div>
        </div>
        <!-- / Color Wheel -->

        <!-- Inputs -->
        <div
          :class="{
            inputs: true,
            'color-picker__inputs': true,
          }"
        >
          <label
            :class="{
              label: true,
              inputs__label: true,
            }"
          >
            <span class="label__title">Name</span>

            <fs-input
              ref="colorNameInput"
              v-model.trim="localColor.name"
              class="color-name-input__input-itself"
              placeholder="Color name"
              :is-disabled="pickerState.provenance.nameLocked"
              :disabled-tooltip="`${pickerState.provenance.libraryName} keeps its colors' names`"
              @click="isUserClickedColorInput = true"
              @input="isUserClickedColorInput = true"
            >
            </fs-input>
          </label>

          <div class="inputs__code-n-group-row">
            <label
              :class="{
                label: true,
                inputs__label: true,
              }"
            >
              <span class="label__title">Code</span>
              <fs-input
                v-model.trim="localColor.code"
                :class="{
                  'label__code-input': true,
                }"
                placeholder="Code"
                :is-disabled="pickerState.provenance.codeLocked"
                :disabled-tooltip="`${pickerState.provenance.libraryName} keeps its colors' codes`"
              />
            </label>

            <label
              :class="{
                label: true,
                inputs__label: true,
              }"
            >
              <span class="label__title">Group</span>
              <color-group-picker v-model="localColor.groupId" @click="isUserClickedGroupInput = true" />
            </label>
          </div>

          <Transition name="hex-rgb-slide">
            <div v-if="showHexRgb" class="inputs__hex-n-rgb-row">
              <label class="label inputs__label">
                <span class="label__title">Hex</span>
                <fs-input
                  v-model="hexInputValue"
                  class="inputs__hex-input"
                  placeholder="#FFFFFF"
                  @focus="isHexEditing = true"
                  @blur="
                    isHexEditing = false;
                    syncHexInput();
                  "
                />
              </label>

              <label class="label inputs__label">
                <span class="label__title">RGB</span>
                <div class="inputs__rgb">
                  <fs-input
                    v-model.number="localColor.r"
                    class="inputs__rgb-input"
                    type="number"
                    inputmode="numeric"
                    placeholder="R"
                    variant="mid-code"
                    text-align-center
                  />
                  <fs-input
                    v-model.number="localColor.g"
                    class="inputs__rgb-input"
                    type="number"
                    inputmode="numeric"
                    placeholder="G"
                    variant="mid-code"
                    text-align-center
                  />
                  <fs-input
                    v-model.number="localColor.b"
                    class="inputs__rgb-input"
                    type="number"
                    inputmode="numeric"
                    placeholder="B"
                    variant="mid-code"
                    text-align-center
                  />
                </div>
              </label>
            </div>
          </Transition>
        </div>
        <!-- / Inputs -->
      </div>
      <!--/ Color Wheel Mode -->
    </div>

    <button-pair
      class="color-picker__button-pair"
      :is-loading="isSaving"
      :is-disabled="isUploadingSwatch"
      @cancel="
        ($event) => {
          fsPopperClose($event);
          emits('close');
        }
      "
      @action="saveColors"
    />
  </div>
</template>

<script setup lang="ts">
import type { ColorDto, ColorSearchResultDto, SetColorArgs } from '@/ApiClient';
import lodashDebounce from 'lodash/debounce';
import { closest } from 'color-diff';
import { computed, nextTick, onMounted, reactive, ref, useTemplateRef, watch } from 'vue';
import { convertHEXtoRGB, convertRGBtoCMYK, convertRGBtoHEX, type ColorDraft } from '@lib/colorManagement';
import {
  applyImplicit,
  copyOf,
  draftFromColor,
  emptyDraft,
  pickExisting,
  pickFromLibrary,
  referenceAfterEdit,
  toSetColorArgs,
  withFlatSwatch,
  withUploadedSwatch,
  type ColorPickerDraft,
} from '@lib/color-picker-draft';
import { imagePath } from '@lib/imgproxy';
import { useAzureBlobFiles } from '@/store/azure-blob-files';
import { fsPopperClose } from '@components/poppers/fs-popper.vue';
import { useColorGroups } from '@/store/color-groups';
import { useColors } from '@/store/colors';

// Components
import ButtonPair from '@components/button-pair.vue';
import CircularSpinnerFull from '@components/circular-spinner-full.vue';
import ColorGroupPicker from './components/color-group-picker.vue';
import ColorPickerIro from './wheel.vue';
import ColorSearchResult from './components/color-search-result.vue';
import FsTooltip from '@components/poppers/fs-tooltip.vue';
import IconButton from '@components/icon-button.vue';
import FsInput from '@components/form-elements/fs-input.vue';
import FsSelect from '@components/form-elements/selects/fs-select/index.vue';

// Icons
import trashCanIcon from '@icons/other/trash-can-icon.svg';
import eyeDropperIcon from '@icons/interface/eye-dropper.svg';
import hashIcon from '@icons/interface/hash.svg';

defineOptions({ name: 'color-picker' });

const {
  color = null,
  initialState = null,
  editColorMode = false,
  saveFunc,
  removeFunc,
  titleText,
  searchLoadsValues = false,
} = defineProps<{
  color?: ColorDto | null;
  /**
   * Reopening the picker on a draft it produced earlier — a colour picked or invented but not
   * saved yet. Passed instead of `color`, because the draft is the only thing that still knows
   * what the values refer to: which colour to reuse, or which library entry to adopt.
   */
  initialState?: ColorPickerDraft | null;
  editColorMode?: boolean;
  /** Called with what was picked, and the draft behind it for callers that show it before it is saved. */
  saveFunc: (args: SetColorArgs, state: ColorPickerDraft) => unknown;
  removeFunc?: () => unknown;
  titleText?: string;
  /**
   * Picking a search result loads it into the editor rather than saving it: for making a colour,
   * where an existing one is a starting point and not the answer.
   */
  searchLoadsValues?: boolean;
}>();

const emits = defineEmits<{ close: [] }>();

const icons = { trashCanIcon, eyeDropperIcon, hashIcon };
const rootRef = useTemplateRef<HTMLElement>('root');
const colorNameInputRef = useTemplateRef<InstanceType<typeof FsInput>>('colorNameInput');

onMounted(() => {
  setTimeout(() => colorNameInputRef.value?.focus(), 100);
  // The libraries the brand has switched on, for the search's source filter.
  useColors().loadLibraries();
});

const title = computed(() => titleText || (editColorMode ? 'Edit color' : 'New color'));

//#region The draft
function openingState(): ColorPickerDraft {
  const state = initialState ?? (color ? draftFromColor(color) : emptyDraft());

  // Suggesting a colour group for an ungrouped colour is the picker's doing, not the user's, so it
  // must not fork the draft — otherwise merely opening the picker on a shared colour would save a
  // copy of it.
  if (state.draft.groupId) return state;
  const closestGroup = useColorGroups().findClosest(state.draft);
  return closestGroup ? applyImplicit(state, { groupId: closestGroup.id }) : state;
}

const pickerState = ref<ColorPickerDraft>(openingState());
const isColorInitiallySet = color !== null || initialState !== null;

// The values being edited. The template binds to these; the watcher below turns a change into a fork.
const localColor = computed<ColorDraft>({
  get: () => pickerState.value.draft,
  // The colour wheel replaces the whole object rather than mutating it, so without a setter
  // dragging it does nothing at all.
  set: (newColor) => Object.assign(pickerState.value.draft, newColor),
});

const isUserClickedColorInput = ref(false);
const isUserClickedGroupInput = ref(false);

const updateLocks = reactive({
  cmyk: false,
  // Set while a picked colour is being applied, so the watchers that would otherwise read it as a
  // user edit (and fork it) stay quiet.
  fork: false,
  automaticColorName: false,
  automaticColorGroup: false,
});

/** Releases the locks once the watchers a pick sets off have run. */
function unlockAfterWatchers(...locks: (keyof typeof updateLocks)[]) {
  nextTick(() => locks.forEach((lock) => (updateLocks[lock] = false)));
}

/**
 * The user changed a value, so the draft stops referring to whatever it came from and a save will
 * create a new colour — until every value is back to what it came from. Editing the colour
 * everyone shares is the Colors page's job.
 */
function fork() {
  if (updateLocks.fork) return;

  Object.assign(
    pickerState.value,
    referenceAfterEdit(pickerState.value, { userChoseGroup: isUserClickedGroupInput.value })
  );
}

watch(
  () => pickerState.value.draft,
  () => {
    updateCmykFromRgb();
    syncHexInput();
    fork();
    updateColorGroupFromRgb();
  },
  { deep: true }
);
//#endregion

//#region Where the values come from
/**
 * The number of the colour behind the draft. Only shown while the draft still refers to one: once a
 * value has been edited the save may fork the colour, and a number that might not survive is worse
 * than none.
 */
const pickedNumber = computed(() =>
  pickerState.value.sourceColorId != null ? pickerState.value.provenance.number : null
);

/** Whether the name in the picker differs from the library's own name for the colour. */
const renamedFromLibrary = computed(() => {
  const { libraryColorName } = pickerState.value.provenance;
  return !!libraryColorName && libraryColorName !== pickerState.value.draft.name;
});

const recodedFromLibrary = computed(() => {
  const { libraryCode } = pickerState.value.provenance;
  return !!libraryCode && libraryCode !== pickerState.value.draft.code;
});

/**
 * Puts the name or code back to the library's. A deliberate choice of name, so the automatic naming
 * must not replace it moments later.
 */
function resetToLibrary(values: Partial<ColorDraft>) {
  isUserClickedColorInput.value = true;
  Object.assign(localColor.value, values);
}
//#endregion

//#region Automatic name, group and CMYK
const debouncedUpdateColorName = lodashDebounce(() => {
  if (!isColorInitiallySet && !isUserClickedColorInput.value) {
    localColor.value.name = closest(localColor.value, useColors().suggestionsColors).name;
  }
}, 220);

watch(
  pickerState,
  () => {
    if (updateLocks.automaticColorName) return;
    debouncedUpdateColorName();
  },
  { deep: true }
);

/**
 * The group keeps following the colour until the user picks one themselves — the same suggestion
 * the picker makes when it opens, kept current as the colour changes.
 */
function updateColorGroupFromRgb() {
  if (updateLocks.automaticColorGroup || isUserClickedGroupInput.value) return;

  const closestGroup = useColorGroups().findClosest(localColor.value);
  if (closestGroup && closestGroup.id !== localColor.value.groupId) localColor.value.groupId = closestGroup.id;
}

function updateCmykFromRgb() {
  if (updateLocks.cmyk) return;

  const cmyk = convertRGBtoCMYK(localColor.value.r, localColor.value.g, localColor.value.b);
  Object.assign(localColor.value, {
    c: Math.round(cmyk.c),
    m: Math.round(cmyk.m),
    y: Math.round(cmyk.y),
    k: Math.round(cmyk.k),
  });
}
//#endregion

//#region Hex and eyedropper
const showHexRgb = ref(false);
const hexInputValue = ref(convertRGBtoHEX(localColor.value.r, localColor.value.g, localColor.value.b));
const isHexEditing = ref(false);

const colorAsHex = computed({
  get: () => convertRGBtoHEX(localColor.value.r, localColor.value.g, localColor.value.b),
  set: (hex: string) => {
    const rgb = convertHEXtoRGB(hex);
    if (rgb) Object.assign(localColor.value, rgb);
  },
});

function syncHexInput() {
  if (isHexEditing.value) return;
  hexInputValue.value = colorAsHex.value;
}

watch(hexInputValue, (newVal) => {
  if (!isHexEditing.value) return;

  const normalized = (newVal || '').trim();
  const hex = normalized.startsWith('#') ? normalized : `#${normalized}`;
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return;

  colorAsHex.value = hex;
});

const supportsEyeDropper = typeof window !== 'undefined' && 'EyeDropper' in window;

async function pickColorFromScreen() {
  if (!supportsEyeDropper) return;

  try {
    const eyeDropper = new (
      window as unknown as { EyeDropper: new () => { open: () => Promise<{ sRGBHex: string }> } }
    ).EyeDropper();
    const result = await eyeDropper.open();
    colorAsHex.value = result.sRGBHex;
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') return;
    throw error;
  }
}
//#endregion

//#region Swatch picture
const isUploadingSwatch = ref(false);

/**
 * Image files dragged over the wheel. Counted rather than flagged: crossing into the wheel's own
 * elements is a dragleave on the container before the dragenter on them, and the hint would flicker.
 */
let swatchDragDepth = 0;
const isDraggingImage = ref(false);
const carriesFiles = (event: DragEvent) => event.dataTransfer?.types.includes('Files') ?? false;

function onSwatchDragEnter(event: DragEvent) {
  if (!carriesFiles(event)) return;
  event.preventDefault();
  swatchDragDepth++;
  isDraggingImage.value = true;
}

// Without preventing dragover the browser takes the drop for itself and opens the file.
function onSwatchDragOver(event: DragEvent) {
  if (!carriesFiles(event)) return;
  event.preventDefault();
  if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
}

function onSwatchDragLeave(event: DragEvent) {
  if (!carriesFiles(event)) return;
  swatchDragDepth = Math.max(0, swatchDragDepth - 1);
  isDraggingImage.value = swatchDragDepth > 0;
}

async function onSwatchDrop(event: DragEvent) {
  if (!carriesFiles(event)) return;
  event.preventDefault();
  swatchDragDepth = 0;
  isDraggingImage.value = false;

  const image = [...(event.dataTransfer?.files ?? [])].find((file) => file.type.includes('image/'));
  if (image) await useSwatchPicture(image);
}

/**
 * Uploads the picture now, and draws the colour with it once the picker saves — on the colour itself
 * or on its fork, wherever the edit lands. The RGB stays as it was.
 */
async function useSwatchPicture(file: File) {
  isUploadingSwatch.value = true;
  try {
    const uploaded = await useAzureBlobFiles().uploadImage(file);
    pickerState.value = withUploadedSwatch(pickerState.value, uploaded.versions[0].path, uploaded.id);
  } finally {
    isUploadingSwatch.value = false;
  }
}

/** Back to the flat colour. The colour keeps its images; the picture just stops standing in for it. */
function useFlatColor() {
  pickerState.value = withFlatSwatch(pickerState.value);
}
//#endregion

//#region Search
const searchInputValue = ref('');
const isSearchResultExpanded = ref(false);
// 'all' | 'palette:<id>' | 'library:<id>'
const searchSource = ref('all');

/** Everything the brand can search: its own colours, its palettes (archived ones aside), and the libraries it is on. */
const searchSourceOptions = computed(() => [
  { label: 'All', value: 'all' },
  ...useColors().pickerPalettes.map((palette) => ({ label: palette.name, value: `palette:${palette.id}` })),
  ...useColors().libraries.map((library) => ({ label: library.name, value: `library:${library.id}` })),
]);

const searchSourceValue = computed(() => {
  const [kind, id] = searchSource.value.split(':');
  if (kind === 'palette') return { paletteId: Number(id) };
  if (kind === 'library') return { libraryId: Number(id) };
  return {};
});

/**
 * Whether the picker is browsing colours rather than mixing one. Narrowing the source counts: asking
 * for a palette is asking to see what is in it, with or without a term to go with it.
 */
const showSearchResults = computed(
  () => searchInputValue.value.length > 0 || isSearchResultExpanded.value || searchSource.value !== 'all'
);

// Closing the search puts the picker back on the colour it is editing, whatever it was looking
// through: the arrow is the way back, so it must not leave a chosen palette holding the view open.
watch(isSearchResultExpanded, (isExpanded) => {
  if (!isExpanded) searchSource.value = 'all';
});

// Narrowing the source opens the search with it, so the arrow that opened the list is the one that
// closes it — otherwise the first click back would only finish opening it.
watch(searchSource, (source) => {
  if (source !== 'all') isSearchResultExpanded.value = true;
});

function clearSearch() {
  searchInputValue.value = '';
  isSearchResultExpanded.value = false;
}

/** A colour was chosen from search — keep a reference to it rather than copying it. */
function pickColor(searchResult: ColorSearchResultDto) {
  // Lock everything so applying the pick doesn't read as a user edit and fork it straight away.
  Object.assign(updateLocks, { cmyk: true, fork: true, automaticColorName: true, automaticColorGroup: true });

  pickerState.value = searchResult.libraryColor
    ? pickFromLibrary(searchResult.libraryColor, {
        name: !searchResult.libraryColor.libraryAllowsNameOverride,
        code: !searchResult.libraryColor.libraryAllowsCodeOverride,
      })
    : pickExisting(searchResult.color!);

  unlockAfterWatchers('cmyk', 'fork', 'automaticColorName', 'automaticColorGroup');
}

/**
 * A search result loaded to start a new colour from. A library colour keeps its reference, so saving
 * it untouched adopts it; one of the brand's own colours is copied, since saving it untouched would
 * otherwise make nothing.
 */
function startFrom(searchResult: ColorSearchResultDto) {
  if (searchResult.libraryColor) {
    pickColor(searchResult);
  } else {
    // The same locks as pickColor, so loading the values doesn't read as the user editing them.
    Object.assign(updateLocks, { cmyk: true, automaticColorName: true, automaticColorGroup: true });
    pickerState.value = copyOf(searchResult.color!);
    unlockAfterWatchers('cmyk', 'automaticColorName', 'automaticColorGroup');
  }

  // The loaded name is the user's choice, and so is the group when the colour has one: neither should
  // be re-suggested over them as the colour changes. A colour without a group — every library colour
  // — gets the nearest one, as the picker does when it opens, and keeps following.
  isUserClickedColorInput.value = true;
  isUserClickedGroupInput.value = pickerState.value.draft.groupId != null;
  if (!isUserClickedGroupInput.value) {
    const closestGroup = useColorGroups().findClosest(pickerState.value.draft);
    if (closestGroup) pickerState.value = applyImplicit(pickerState.value, { groupId: closestGroup.id });
  }
  clearSearch();
}

async function selectSearchResult(searchResult: ColorSearchResultDto) {
  if (searchLoadsValues) {
    startFrom(searchResult);
    return;
  }
  pickColor(searchResult);
  await saveColors();
}
//#endregion

//#region Save and remove
const isSaving = ref(false);

function close() {
  fsPopperClose(rootRef.value);
  emits('close');
}

async function saveColors() {
  isSaving.value = true;
  try {
    await saveFunc(toSetColorArgs(pickerState.value), pickerState.value);
  } finally {
    isSaving.value = false;
  }
  close();
}

async function removeColor() {
  isSaving.value = true;
  try {
    await removeFunc?.();
  } finally {
    isSaving.value = false;
  }
  close();
}
//#endregion
</script>

<style scoped lang="scss">
@import '@styles/variables';
@import '@styles/text';

// Label --------------------------------------------------------------
.label {
  &__title {
    display: block;
    @include overline--subtle;
    margin: 0 0 0.4rem 2rem;
  }

  &__code-input {
    width: 20rem;
  }
}

// Inputs --------------------------------------------------------------
.inputs {
  &__label {
  }

  &__code-n-group-row {
    width: calc(100% - 1rem);
    margin-top: 1.2rem;
    display: flex;
    justify-content: space-between;
  }

  &__hex-n-rgb-row {
    width: calc(100% - 1rem);
    margin-top: 1.2rem;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  &__hex-input {
    width: 12.2rem;
  }

  &__rgb {
    display: flex;
    gap: 0.8rem;
  }

  &__rgb-input {
    width: 7rem;
  }
}

// Title Row --------------------------------------------------------------
.title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-content: center;

  &__title-text {
    font-size: 2rem;
    line-height: 2.4rem;
    font-family: 'Montserrat', sans-serif;
    color: $c-dark;
  }

  &__button-container {
    display: flex;
  }

  &__toggle-button--active {
    opacity: 1;
    background: rgba($c-dark, 0.08);
    border-radius: 0.4rem;
  }
}

// The search term and what it looks through, joined into one control — the same pair the
// components table's colour cell searches with. The source stays narrow until it is opened,
// because the term is what the reader is typing and the source only qualifies it.
.search-form {
  position: relative;
  display: flex;

  &__input {
    flex-grow: 1;
    min-width: 0;
  }

  &__select {
    width: 7rem;
    flex-shrink: 0;
  }
}

// Where the draft's values come from. Stated rather than offered: neither the colour number nor
// the library entry is editable from the picker.
.provenance {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;

  &__row {
    display: flex;
    align-items: baseline;
    gap: 0.8rem;
    font-size: 1.2rem;
    line-height: 1.4rem;
  }

  &__label {
    flex-shrink: 0;
    width: 8.8rem;
    color: rgba($c-dark, 0.5);
  }

  &__value {
    min-width: 0;
    color: $c-dark;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;

    &--pending {
      color: rgba($c-dark, 0.5);
      font-style: italic;
    }
  }

  &__reset {
    text-decoration: underline;
    cursor: pointer;
    transition: opacity 0.15s ease;

    &:hover {
      opacity: 0.5;
    }
  }
}

// Color Picker --------------------------------------------------------------
.color-picker {
  width: 35.6rem;
  padding: 2.4rem 0;

  &__title-row {
    margin: 0 3.2rem 2.4rem;
  }

  &__search {
    margin: 0 3.2rem;
  }

  &__provenance {
    margin: 1.2rem 3.2rem 0;
  }

  &__content-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
  }

  &__content {
    width: 100%;

    &--search-result {
      margin: 0 1.7rem 0 2.2rem;
    }
  }

  &__color-wheel-container {
    padding: 3.6rem 0 1.8rem;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
  }

  &__color-wheel-container--drop-target {
    outline: 0.2rem dashed $c-blue;
    outline-offset: -0.8rem;
    border-radius: 1.6rem;
  }

  // The same footprint as the wheel it stands in for, so the inputs below don't move.
  &__swatch {
    position: relative;
    width: 26.8rem;
    height: 26.8rem;
  }

  &__swatch-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 1.2rem;
    box-shadow: inset 0 0 0 1px rgba($c-dark, 0.1);
  }

  &__swatch-remove {
    position: absolute;
    top: 0.8rem;
    right: 0.8rem;
  }

  &__swatch-drop-hint {
    position: absolute;
    inset: 0.8rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 1.2rem;
    background-color: rgba(#fff, 0.85);
    color: $c-blue;
    font-weight: 600;
    // Drag events go through to the container, which owns the drop.
    pointer-events: none;
  }

  &__eyedropper-button {
    position: absolute;
    bottom: 2.5rem;
    right: 8.5rem;
  }

  &__inputs {
    width: 100%;
    padding: 0 3.2rem;
  }

  &__button-pair {
    margin-top: 3.2rem;
  }
}

// Hex/RGB slide transition
.hex-rgb-slide-enter-active,
.hex-rgb-slide-leave-active {
  transition: all 0.2s ease;
  overflow: hidden;
}

.hex-rgb-slide-enter-from,
.hex-rgb-slide-leave-to {
  max-height: 0;
  margin-top: 0;
  opacity: 0;
}

.hex-rgb-slide-enter-to,
.hex-rgb-slide-leave-from {
  max-height: 10rem;
  opacity: 1;
}
</style>
